import React, { useEffect, useMemo, useState } from 'react';

import { LayerContext } from 'state/LayerContext';
import { useGetDatesQuery } from 'state/apiSlice';
import { useAppSelector } from 'state/hooks';
import {
  selectMaximize,
  selectProject,
  selectTargetDate,
} from 'state/workflowSlice';

import ScreenshotChart from 'features/chart/ScreenshotChart';
import { HighchartsSyncProvider } from 'features/chart/SyncedHighchartsReact';
import { ChartHeader } from 'features/workflow_nrt/ChartHeader';
import { workflowStyles } from 'features/workspace/workspaceUtils';

import { MapContextProvider } from '../map/MapContext';

import styles from '../workspace/Workspace.module.css';

export function VisualisationRegion({
  children,
  graph_title,
  unselected_state_fn,
}: {
  children: React.ReactNode;
  graph_title: string;
  unselected_state_fn: () => React.ReactNode | undefined;
}) {
  const maximize = useAppSelector(selectMaximize);

  const { data_viz_style, header_style } = workflowStyles(maximize);

  const unselected_state = unselected_state_fn();
  if (unselected_state) {
    return unselected_state as any;
  }

  return (
    <div className={data_viz_style}>
      <div className={header_style}>
        <ChartHeader graph_title={graph_title} />
      </div>
      <div className={styles.chart_container_inner}>{children}</div>
    </div>
  );
}

export function useAutoSetLayerFromTargetDate() {
  const project = useAppSelector(selectProject);
  const targetDate = useAppSelector(selectTargetDate);
  const { currentData: datesInfo } = useGetDatesQuery(project);
  const [layer, setLayer] = useState(null);
  const memoizedLayer = useMemo(() => ({ layer, setLayer }), [layer]);

  useEffect(() => {
    if (datesInfo) {
      if (targetDate) {
        const new_layer =
          datesInfo && targetDate
            ? datesInfo.find((info) => info.date === targetDate)?.layer
            : null;
        if (new_layer !== layer) {
          console.log(
            `Set new layer ${new_layer} for targetDate ${targetDate}`,
          );
          setLayer(new_layer);
        }
      }
    } else {
      console.log('Clearing layer due to changes in input');
      setLayer(undefined);
    }
  }, [datesInfo, targetDate, setLayer, project]);

  return memoizedLayer;
}

export function WorkflowNrtSkeleton({
  vizRegionComponent,
  mapComponent,
  taskBarComponent,
  enableScreenshot = true,
}: {
  vizRegionComponent: React.ReactNode;
  mapComponent: React.ReactNode;
  taskBarComponent: React.ReactNode;
  enableScreenshot?: boolean;
}) {
  const maximize = useAppSelector(selectMaximize);

  // memoize so this doesn't change every render.
  const memoizedLayer = useAutoSetLayerFromTargetDate();

  // ======================

  const { map_style } = workflowStyles(maximize);

  return (
    <LayerContext.Provider value={memoizedLayer}>
      <MapContextProvider>
        <HighchartsSyncProvider>
          <div className={styles.workspace_container}>
            <div className={styles.workspace_content}>
              <div className={map_style}>{mapComponent}</div>
              {vizRegionComponent}
            </div>
            {taskBarComponent}
          </div>
          {enableScreenshot && (
            <ScreenshotChart>{vizRegionComponent}</ScreenshotChart>
          )}
        </HighchartsSyncProvider>
      </MapContextProvider>
    </LayerContext.Provider>
  );
}
